'use client';

import React, { useMemo, useState } from 'react';
import type { QuestionConfig, AnswerData, ValidationRules } from '../types';
import { useTranslation } from '@/app/i18n/client';

interface EmailQuestionProps {
  config: QuestionConfig;
  value: AnswerData;
  onChange: (value: AnswerData) => void;
  validationRules?: ValidationRules;
  error?: string;
  userEmail?: string | null; // User email from session
  onEnterPress?: () => void; // Callback when Enter is pressed
}

export default function EmailQuestion({
  config,
  value,
  onChange,
  validationRules,
  error,
  userEmail,
  onEnterPress,
}: EmailQuestionProps) {
  const { t } = useTranslation();
  const { lang } = useTranslation();

  // Helper function to get text in correct language
  const getText = (th: string | undefined, en: string | undefined): string => {
    if (lang === 'en') {
      return en || th || '';
    }
    return th || en || '';
  };

  const emailValue = value?.value || '';
  const [isValidating, setIsValidating] = useState(false);

  // Check if email is a dummy guest email (pattern: <random>@dummy.com)
  const isDummyEmail = (email: string | null | undefined): boolean => {
    if (!email) return false;
    return /@dummy\.com$/i.test(email);
  };

  // Only use real emails (not dummy guest emails)
  const realUserEmail = userEmail && !isDummyEmail(userEmail) ? userEmail : null;

  // Auto-fill email from session if available and it's a real email
  React.useEffect(() => {
    if (realUserEmail) {
      onChange({ value: realUserEmail });
    }
  }, [realUserEmail, emailValue, onChange]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange({ value: e.target.value });
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && onEnterPress) {
      e.preventDefault();
      
      // Check if email is valid before allowing Enter to proceed
      if (!emailValue || !validationStatus.isValid) {
        return;
      }
      
      setIsValidating(true);
      onEnterPress();
      // Reset validation state after processing
      setTimeout(() => setIsValidating(false), 300);
    }
  };

  // Validate email pattern
  const validationStatus = useMemo(() => {
    if (!emailValue) {
      return { isValid: false, message: null };
    }

    // Use validation pattern from validationRules if provided
    const pattern = validationRules?.pattern;
    if (pattern) {
      try {
        const regex = new RegExp(pattern);
        const isValid = regex.test(emailValue);
        return {
          isValid,
          message: !isValid && validationRules?.message ? validationRules.message : null
        };
      } catch (e) {
        console.error('Invalid regex pattern:', pattern);
      }
    }

    // Fallback to basic email validation
    const basicEmailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    const isValid = basicEmailRegex.test(emailValue);
    return {
      isValid,
      message: !isValid ? getText('กรุณากรอกอีเมลที่ถูกต้อง', 'Please enter a valid email address') : null
    };
  }, [emailValue, validationRules]);

  // Helper text for user guidance
  const getHelperText = () => {
    if (emailValue.length === 0) {
      return getText('กรุณากรอกอีเมลของคุณ', 'Please enter your email');
    }
    if (!validationStatus.isValid && validationStatus.message) {
      return validationStatus.message;
    }
    return null;
  };

  const helperText = getHelperText();

  return (
    <div className="w-full max-w-lg">
      <input
        type="email"
        value={realUserEmail || emailValue}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        placeholder={config.placeholder || 'example@email.com'}
        disabled={!!realUserEmail}
        className={`w-full px-6 pt-4 pb-3 border-2 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#2A807B] focus:border-transparent transition-all ${
          error || (emailValue && !validationStatus.isValid) ? 'border-red-500' : 'border-gray-200'
        } ${realUserEmail ? 'bg-gray-100 cursor-not-allowed' : ''}`}
      />
      {helperText && (
        <p className={`text-sm mt-1 ${
          emailValue.length === 0 ? 'text-[#2A807B]' : 'text-red-600'
        }`}>
          {helperText}
        </p>
      )}
      {error && <p className="text-red-600 text-sm text-center">{error}</p>}
    </div>
  );
}
